import { useEffect, useState } from 'react';

import { fetchRecords } from '../../../services/syntiro_honey/data_ingestion.service';

/** Action-oriented triage counts shown by the summary chips, or null before first load. */
export interface RecordStats {
  /** Records with a trusted, deterministic metric match (no review needed). */
  exact: number;
  /** Records with an AI-suggested metric the reviewer should confirm. */
  suggested: number;
  /** Records with no metric resolved yet. */
  unmapped: number;
  /** Records mapped by a learned rule (a subset of `exact` — the loop's payoff). */
  learned: number;
}

/**
 * Fetches the triage counts behind the summary chips via three parallel
 * count-only list queries (`limit: 1`, reading `total`) by mapping status —
 * the action-oriented split (confirm vs fix vs done). Cheap enough for the review
 * page; replaced by a dedicated stats endpoint if it ever isn't. Re-fetches
 * whenever `refreshKey` changes (the panel bumps it on any mutation/refresh).
 * @param {number} refreshKey Bump to re-fetch.
 * @returns {RecordStats | null} The counts, or null until the first load lands.
 */
export function useRecordStats(refreshKey: number): RecordStats | null {
  const [stats, setStats] = useState<RecordStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    const countOf = async (query: Parameters<typeof fetchRecords>[0]): Promise<number> => {
      const page = await fetchRecords({ ...query, limit: 1, offset: 0 });
      return page.total;
    };
    Promise.all([
      countOf({ mappingStatus: 'EXACT' }),
      countOf({ mappingStatus: 'SUGGESTED' }),
      countOf({ mappingStatus: 'UNMAPPED' }),
      countOf({ mappedFromAlias: true })
    ])
      .then(([exact, suggested, unmapped, learned]) => {
        if (!cancelled) {
          setStats({ exact, suggested, unmapped, learned });
        }
      })
      // The chips are an at-a-glance extra; a failed count must not error the
      // whole review page, so the previous (or empty) state is simply kept.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return stats;
}
